import { Fragment } from 'react';
import { GlassCard, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { vizToneColor } from '@/versions/v1/modules/_viz';
import { Commentable } from '@/shared/comments/Commentable';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { CARD_TABLE, DowntimeCard } from '../downtime/DowntimeCard';
import { meetingBand, meetingBandFooter } from './MeetingBoard.transform';
import {
  basisFigure,
  formatDeltaPct,
  formatFigure,
  groupBasisLabel,
  perfRows,
  windowsFor,
  type PerfCell,
} from './PerfTab.transform';

/** The page's lead band (locked comp `meeting.html` B): the four 24 h tiles
 * in one hairline-split card, each figure over its comparison, and a footer
 * carrying the plant's bottleneck read (subject and facts, A's structured
 * read) beside the plan misses that raised a 3W this window, grouped by the
 * variation that raised them. Every value comes from
 * `MeetingBoard.transform.ts`; this component lays them out. */
export function PerfBand() {
  const { state } = useScenario();
  const advisory = useAdvisory();
  const cells = meetingBand(state);
  const footer = meetingBandFooter(state, advisory);

  return (
    <GlassCard
      as="section"
      tone="matte"
      aria-label="Last 24 hours against plan"
      data-meeting-band
      className="overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
    >
      <div className="grid grid-cols-2 xl:grid-cols-4">
        {cells.map((cell) => (
          <div
            key={cell.id}
            data-band-cell={cell.id}
            className="relative flex min-w-0 flex-col gap-1 border-b border-r border-[var(--hairline)] px-4 pb-1.5 pt-2 last:border-r-0 xl:border-b-0"
          >
            <span aria-hidden className="absolute inset-x-0 top-0 h-[3px]" style={{ backgroundColor: vizToneColor(cell.tone) }} />
            <span className="truncate text-xs font-semibold uppercase tracking-[0.07em] text-muted">
              {cell.label}
              {cell.day ? ' · 24 h' : ''}
            </span>
            <span className="text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
              {cell.figure}
              {cell.unit ? <span className="ml-1 text-xs font-semibold text-muted">{cell.unit}</span> : null}
            </span>
            <span className="truncate text-xs leading-snug tabular-nums text-muted">
              <span className="font-semibold text-ink-secondary">{cell.lead}</span>
              {cell.rest ? ` ${cell.rest}` : ''}
            </span>
          </div>
        ))}
      </div>
      <div
        data-band-footer
        className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3 gap-y-1 border-t border-[var(--hairline)] px-4 py-1.5 text-xs"
      >
        <p className="contents">
          <span className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted">Bottleneck</span>
          <span className="min-w-0 leading-snug text-ink-secondary">
            {footer.bottleneck.subject ? (
              <span className="font-semibold text-ink-primary">{footer.bottleneck.subject}</span>
            ) : null}
            {footer.bottleneck.facts.map((fact, index) => (
              <Fragment key={fact}>
                {footer.bottleneck.subject || index > 0 ? <span className="text-muted"> · </span> : null}
                {fact}
              </Fragment>
            ))}
          </span>
        </p>
        <p className="contents">
          <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.07em] text-muted">
            Variations vs plan · {footer.variations.length}
          </span>
          <span className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            {footer.variations.map((variation) => (
              <span key={variation.id} className="inline-flex items-baseline gap-1 whitespace-nowrap text-ink-secondary">
                {variation.label}
                {variation.threeWIds.map((id) => (
                  <span key={id} className={REF_CHIP}>
                    {id}
                  </span>
                ))}
              </span>
            ))}
          </span>
        </p>
      </div>
    </GlassCard>
  );
}

/** A 3W or CCC id as a neutral reference (comp `.ref`): the priority pill
 * carries a row's state, so the id itself never takes a state colour. */
export const REF_CHIP =
  'inline-block whitespace-nowrap rounded-md border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-1.5 font-mono text-[11px] leading-[1.35] text-ink-secondary';

/** The `Measured performance` tab — every measured quantity over 24 h / WTD /
 * MTD with the basis it is judged against and the signed deviation. The
 * basis kind is said once per group (`vs budget` / `vs spec`); the 24 h
 * column is the baseline the band above summarises. Rows come from
 * `PerfTab.transform.ts`; this file lays them out and nothing else. */
export function PerfTab() {
  const { state } = useScenario();
  const windows = windowsFor(state);
  const groups = perfRows(state);

  return (
    <Commentable name="PerfTab">
      <DowntimeCard title="Measured performance" meta="24 h, week and month to date" className="px-0 pb-0 pt-3 [&>header]:px-4" aria-label="Measured performance">
        <div className={cn(CARD_TABLE, 'border-t border-[var(--hairline)] [&_td:first-child]:pl-4 [&_th:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:last-child]:pr-4')}>
          <Table wrap={false} className="tabular-nums">
            <THead>
              <TR>
                <TH className="min-w-[160px]">Measure</TH>
                {windows.map((window) => (
                  <Fragment key={window.key}>
                    <TH className="border-l border-divider text-right">{window.label}</TH>
                    <TH className="text-right">Basis</TH>
                    <TH className="text-right">Δ</TH>
                  </Fragment>
                ))}
              </TR>
            </THead>
            <TBody>
              {groups.map((group) => (
                <Fragment key={group.group}>
                  <TR className="bg-[var(--surface-1)]">
                    <TD
                      colSpan={1 + windows.length * 3}
                      className="!py-1 text-[10px] font-semibold uppercase tracking-[0.07em] !text-muted"
                    >
                      {group.group}
                      <span className="ml-2 font-medium normal-case tracking-normal">{groupBasisLabel(group)}</span>
                    </TD>
                  </TR>
                  {group.rows.map((row) => (
                    <TR key={row.id}>
                      <TD className="whitespace-nowrap">
                        <span className="text-ink-primary">{row.label}</span>
                        <span className="ml-1 text-xs text-muted">{row.unit}</span>
                      </TD>
                      {row.cells.map((cell, index) => (
                        <Fragment key={windows[index]!.key}>
                          <TD className="whitespace-nowrap border-l border-divider text-right font-semibold !text-ink-primary">
                            {formatFigure(cell.actual, row.decimals)}
                          </TD>
                          <TD className="whitespace-nowrap text-right !text-muted">{basisFigure(cell)}</TD>
                          <ToneCell cell={cell} />
                        </Fragment>
                      ))}
                    </TR>
                  ))}
                </Fragment>
              ))}
            </TBody>
          </Table>
        </div>
      </DowntimeCard>
    </Commentable>
  );
}

/** The deviation cell — the number carries the tone, the basis cell carries
 * the comparator. Colour is meaning: `--state-*` only, never ink. */
function ToneCell({ cell }: { cell: PerfCell }) {
  return (
    <TD className="whitespace-nowrap text-right">
      <span className="font-semibold" style={{ color: vizToneColor(cell.tone) }}>
        {formatDeltaPct(cell.deltaPct)}
      </span>
    </TD>
  );
}
